import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios, { isAxiosError } from 'axios';
import { format } from 'date-fns';
import axiosInstance from '../utils/axios-auth';
import { normalizeApiResponse, handleApiError } from '../utils/api-helpers';
// Import schedule utility functions
import { toLocalDateKey, normalizeOverrideDateKey, buildLocalLessonIsoRange } from '../utils/scheduleUtils';
import clientEnv from '../env';
// Validate that REACT_APP_API_URL is set (axiosInstance handles the base URL)
if (!clientEnv.REACT_APP_API_URL) {
    console.warn('REACT_APP_API_URL is not set. API calls may fail.');
}
// Configure API request timeout (5 seconds) - used for most API calls
const API_TIMEOUT = 5000;
// Schedule/availability requests need longer timeout (CSRF + session + cold start)
const SCHEDULE_API_TIMEOUT = 15000;
// Use mock data in development if specified
const USE_MOCK_DATA = clientEnv.NODE_ENV === 'development' && clientEnv.REACT_APP_USE_MOCK_DATA === 'true';
// Helper function to ensure subjects is always an array
const ensureSubjectsArray = (subjects) => {
    if (typeof subjects === 'string') {
        return subjects.split(',').map(subject => subject.trim()).filter(Boolean);
    }
    return subjects;
};
// Helper function to get display name from first and last name
export const getFullName = (first_name, last_name) => {
    return `${first_name || ''} ${last_name || ''}`.trim();
};
// Async thunk for searching tutors
export const searchTutors = createAsyncThunk('tutor/searchTutors', async ({ search_term, subject, date }, { rejectWithValue }) => {
    try {
        // Build query parameters - ensure proper encoding
        const params = new URLSearchParams();
        if (search_term) {
            params.append('term', search_term);
        }
        if (subject && subject !== 'all') {
            params.append('subject', subject);
        }
        if (date) {
            params.append('date', typeof date === 'string' ? date : format(date, 'yyyy-MM-dd'));
        }
        // Use /api/tutors/search endpoint (public endpoint)
        const response = await axiosInstance.get(`/tutors/search?${params.toString()}`);
        // Process API response using normalized helper
        const tutors = normalizeApiResponse(response);
        // Ensure proper data formatting and snake_case
        return tutors.map((tutor) => normalizeApiPayload(tutor, {
            id: '',
            first_name: '',
            last_name: '',
            email: '',
            bio: '',
            hourly_rate: 0,
            subjects: [],
            // profile_image: '', // Removed as per interface alignment
            // average_rating: 0 // Removed as per interface alignment
        }));
    }
    catch (error) {
        console.error('Error searching tutors:', error);
        if (isAxiosError(error)) {
            // Log more details for debugging
            console.error('Response status:', error.response?.status);
            console.error('Response data:', error.response?.data);
        }
        return rejectWithValue(handleApiError(error));
    }
});
// Async thunk for fetching tutor availability
export const fetchTutorAvailability = createAsyncThunk('tutor/fetchAvailability', async ({ tutorId, date }, { rejectWithValue }) => {
    try {
        const formattedDate = date instanceof Date
            ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
            : date;
        // Use the public availability endpoint: /api/tutors/:tutorId/availability
        const response = await axiosInstance.get(`/tutors/${tutorId}/availability?date=${formattedDate}`);
        // Process API response
        const availabilityData = normalizeApiResponse(response);
        // The API returns slots as an array of strings when date is provided
        // Return available time slots
        if (Array.isArray(availabilityData)) {
            return availabilityData;
        }
        else if (availabilityData && availabilityData.slots) {
            return availabilityData.slots;
        }
        else if (availabilityData && Array.isArray(availabilityData.data)) {
            // Handle case where data is nested in response.data
            return availabilityData.data;
        }
        // If no slots found, return empty array
        console.warn('API returned unexpected format for availability, returning empty slots');
        return [];
    }
    catch (error) {
        console.error('Error fetching tutor availability:', error);
        if (isAxiosError(error)) {
            // Log more details for debugging
            console.error('Response status:', error.response?.status);
            console.error('Response data:', error.response?.data);
            // If API returns 404, return empty array (tutor may not have availability set up)
            if (error.response?.status === 404) {
                console.warn('Tutor availability not found, returning empty slots');
                return [];
            }
        }
        return rejectWithValue(handleApiError(error));
    }
});
// Async thunk for booking a tutor
export const bookTutor = createAsyncThunk('tutor/bookTutor', async (params, { rejectWithValue }) => {
    try {
        let tutorId;
        let bookingParams;
        // Handle both old and new parameter formats
        if (typeof params === 'string') {
            tutorId = params;
        }
        else {
            tutorId = params.tutorId;
            bookingParams = params.params;
        }
        let response;
        if (bookingParams) {
            const durationHours = bookingParams.durationHours ?? 1;
            const range = buildLocalLessonIsoRange(bookingParams.date, bookingParams.timeSlot, durationHours);
            response = await axiosInstance.post(`/tutors/${tutorId}/book`, {
                date: bookingParams.date,
                timeSlot: bookingParams.timeSlot,
                start_time: range.start_time,
                end_time: range.end_time
            });
        }
        else {
            response = await axiosInstance.post(`/tutors/${tutorId}/book`);
        }
        // Handle different response formats consistently
        const responseData = normalizeApiResponse(response);
        // Return booking ID
        if (responseData?.id) {
            return responseData.id;
        }
        else if (typeof responseData === 'string') {
            return responseData;
        }
        return 'booking-created';
    }
    catch (error) {
        return rejectWithValue(handleApiError(error));
    }
});
// Overall timeout for schedule fetch (covers interceptor delays - getCSRFToken, getSession)
const SCHEDULE_FETCH_OVERALL_TIMEOUT = 40000;
// Async thunk for fetching tutor schedule
export const fetchTutorSchedule = createAsyncThunk('tutor/fetchSchedule', async (_, { rejectWithValue, getState }) => {
    // Get the current user ID from the auth state if available
    const state = getState();
    const userId = state.auth?.user?.id || null;
    if (!userId) {
        console.warn('No user ID available when fetching tutor schedule');
        return [];
    }
    const token = localStorage.getItem('access_token');
    if (!token) {
        console.warn('No auth token found in localStorage');
        return [];
    }
    const fetchSchedule = async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), SCHEDULE_API_TIMEOUT);
        const commonHeaders = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
        const commonParams = { tutor_id: userId };
        // Single API call - /tutors/availability and /tutors/schedule return identical data
        let availResult;
        try {
            availResult = await Promise.allSettled([
                axiosInstance.get('/tutors/availability', {
                    headers: commonHeaders,
                    params: commonParams,
                    signal: controller.signal
                })
            ]).then(([r]) => r);
        }
        finally {
            clearTimeout(timeoutId);
        }
        // Helper to build schedule items from availability data.
        // normalizeApiResponse may return either the full envelope { success, data: {...} }
        // or the already-unwrapped inner data object { date_overrides, weekly_schedule, ... }.
        // Handle both shapes so the function works regardless of unwrapping depth.
        const buildAvailItems = (rawData) => {
            const availItems = [];
            if (!rawData)
                return availItems;
            const inner = rawData.data ?? rawData;
            const dateOverrides = inner.date_overrides;
            const lessonDuration = inner.lesson_duration || 30;
            const weeklySchedule = inner.weekly_schedule || {};
            if (dateOverrides && Array.isArray(dateOverrides) && dateOverrides.length > 0) {
                dateOverrides.forEach((override, index) => {
                    const d = normalizeOverrideDateKey(override.date);
                    if (d && override.slots && override.slots.length > 0) {
                        availItems.push({
                            id: `avail-${index}`,
                            date: d,
                            slots: override.slots,
                            lesson_duration: lessonDuration,
                            status: 'available',
                            severity: 'low'
                        });
                    }
                });
            }
            for (const [day, slots] of Object.entries(weeklySchedule)) {
                if (Array.isArray(slots) && slots.length > 0) {
                    const today = new Date();
                    const targetDay = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].indexOf(day.toLowerCase());
                    if (targetDay !== -1) {
                        const daysToAdd = (targetDay + 7 - today.getDay()) % 7;
                        const targetDate = new Date(today);
                        targetDate.setDate(today.getDate() + daysToAdd);
                        const dateString = toLocalDateKey(targetDate);
                        const hasOverride = availItems.some(item => item.date === dateString);
                        if (!hasOverride) {
                            availItems.push({
                                id: `weekly-${day}`,
                                date: dateString,
                                slots: slots,
                                lesson_duration: lessonDuration,
                                status: 'available',
                                severity: 'low'
                            });
                        }
                    }
                }
            }
            return availItems;
        };
        // Prefer availability result if successful and has items
        if (availResult.status === 'fulfilled') {
            try {
                const availabilityData = normalizeApiResponse(availResult.value);
                const availItems = buildAvailItems(availabilityData);
                if (availItems.length > 0)
                    return availItems;
            }
            catch (e) {
                if (e?.response?.status !== 406) {
                    console.warn('Could not process availability response:', e);
                }
            }
        }
        else if (availResult.reason && availResult.reason?.response?.status !== 406) {
            console.warn('Could not fetch from availability endpoint:', availResult.reason);
        }
        // Availability returned fulfilled but empty (no schedule data) - valid state
        if (availResult.status === 'fulfilled') {
            return [];
        }
        // Availability failed - use rejection for error handling
        const apiError = availResult.reason;
        // Handle 406 Not Acceptable errors quietly - this is common with Supabase
        if (apiError?.response?.status === 406) {
            console.info('API returned 406 Not Acceptable - this may be expected with Supabase');
            return [];
        }
        // Handle timeout/abort errors specially
        if (apiError?.name === 'AbortError') {
            console.warn('API request timed out, using mock data');
            return createMockScheduleData();
        }
        // Handle authentication errors
        if (apiError?.response?.status === 401) {
            console.error('Authentication failed when fetching schedule: Token may be invalid or expired');
            return [];
        }
        // Special case: If it's a 404 with a message about no schedule
        if (apiError?.response?.status === 404) {
            console.info('No schedule found, treating as empty schedule');
            return [];
        }
        // Handle 204 No Content or other "empty but valid" responses
        if (apiError?.response?.status === 204 ||
            (apiError?.response?.status === 200 && (!apiError?.response?.data || apiError?.response?.data?.length === 0))) {
            console.info('Empty but valid response received');
            return [];
        }
        // Handle connection errors
        if (apiError?.code === 'ECONNABORTED' || apiError?.code === 'ERR_NETWORK') {
            console.warn('Network connection error, using mock data:', apiError);
            return createMockScheduleData();
        }
        // For API errors during development, use mock data
        if (process.env.NODE_ENV === 'development') {
            console.warn('API error in development mode, using mock schedule data:', apiError);
            return createMockScheduleData();
        }
        throw apiError;
    };
    const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
            reject(new Error('Schedule fetch timed out - request may be blocked by auth/session'));
        }, SCHEDULE_FETCH_OVERALL_TIMEOUT);
    });
    try {
        return await Promise.race([fetchSchedule(), timeoutPromise]);
    }
    catch (error) {
        // Log error details for debugging
        console.error('Failed to fetch tutor schedule:', error?.response || error);
        // On timeout or interceptor hang, return empty array so UI can render
        if (error?.message?.includes('timed out') || error?.message?.includes('blocked')) {
            console.warn('Schedule fetch timed out, returning empty schedule');
            return [];
        }
        const errorMessage = error?.response?.data?.message ||
            error?.message ||
            'An unknown error occurred fetching tutor schedule';
        return rejectWithValue(errorMessage);
    }
});
// Helper function to create mock schedule data
const createMockScheduleData = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    // Create mock schedule data for development
    return [
        {
            id: '1',
            date: toLocalDateKey(today),
            slots: [
                { start_time: '09:00', end_time: '10:00' },
                { start_time: '11:00', end_time: '12:00' }
            ],
            lesson_duration: 60,
            studentName: '',
            timeSlot: '',
            status: '',
            severity: ''
        },
        {
            id: '2',
            date: toLocalDateKey(tomorrow),
            slots: [
                { start_time: '14:00', end_time: '15:00' },
                { start_time: '16:00', end_time: '17:00' }
            ],
            lesson_duration: 60,
            studentName: '',
            timeSlot: '',
            status: '',
            severity: ''
        }
    ];
};
// Async thunk for updating tutor availability
export const updateTutorAvailability = createAsyncThunk('tutor/updateAvailability', async (params, { rejectWithValue }) => {
    try {
        // Support both old and new formats for backward compatibility
        let apiParams;
        if (params.weekly_schedule || params.date_overrides || params.lesson_duration) {
            // New format - validate the payload structure
            if (!params.weekly_schedule ||
                !params.date_overrides ||
                params.date_overrides.length === 0 ||
                typeof params.lesson_duration !== 'number') {
                return rejectWithValue('Invalid availability payload format. Please check required fields.');
            }
            // Validate that slots have proper format
            const hasValidWeeklySlots = Object.values(params.weekly_schedule).every(slots => Array.isArray(slots) && slots.every((slot) => typeof slot === 'object' &&
                typeof slot.start_time === 'string' &&
                typeof slot.end_time === 'string' &&
                /^\d{2}:\d{2}$/.test(slot.start_time) &&
                /^\d{2}:\d{2}$/.test(slot.end_time)));
            if (!hasValidWeeklySlots) {
                return rejectWithValue('Invalid time slot format in weekly schedule. Expected format: HH:MM');
            }
            const hasValidDateOverrides = params.date_overrides.every(override => typeof override === 'object' &&
                typeof override.date === 'string' &&
                /^\d{4}-\d{2}-\d{2}$/.test(override.date) &&
                Array.isArray(override.slots) &&
                override.slots.every((slot) => typeof slot === 'object' &&
                    typeof slot.start_time === 'string' &&
                    typeof slot.end_time === 'string' &&
                    /^\d{2}:\d{2}$/.test(slot.start_time) &&
                    /^\d{2}:\d{2}$/.test(slot.end_time)));
            if (!hasValidDateOverrides) {
                return rejectWithValue('Invalid date override format. Expected date format: YYYY-MM-DD and time format: HH:MM');
            }
            apiParams = {
                weekly_schedule: params.weekly_schedule,
                date_overrides: params.date_overrides,
                lesson_duration: params.lesson_duration
            };
        }
        else {
            // Old format - convert to new format
            if (!params.date || !params.slots || !params.duration) {
                return rejectWithValue('Missing required fields for availability update');
            }
            apiParams = {
                date: params.date,
                slots: params.slots,
                duration: params.duration
            };
        }
        try {
            // Get current auth token from localStorage
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('Authentication required');
            }
            // Get CSRF token first
            let csrfToken = null;
            try {
                const csrfResponse = await axiosInstance.get('/security/csrf-token', {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    withCredentials: true
                });
                csrfToken = csrfResponse.data.csrfToken;
            }
            catch (csrfError) {
                console.warn('Failed to get CSRF token:', csrfError);
                // Continue without CSRF token for debugging
            }
            // Log the full URL for debugging
            // Create controller for timeout handling
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000);
            // Prepare headers with CSRF token if available
            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };
            if (csrfToken) {
                headers['X-CSRF-Token'] = csrfToken;
            }
            // Make the API call to update availability with auth token and CSRF token
            const response = await axiosInstance.post('/tutors/availability', apiParams, {
                headers,
                signal: controller.signal,
                withCredentials: true
            });
            // Clear the timeout
            clearTimeout(timeoutId);
            // Log the successful response for debugging
            // Format the response for consistent state updates
            const normalizedResponse = normalizeApiResponse(response);
            // Return properly formatted data to the reducer
            if (normalizedResponse && typeof normalizedResponse === 'object') {
                // Add a refresh flag to indicate that schedule should be refreshed
                return {
                    ...normalizedResponse,
                    needsRefresh: true
                };
            }
            else {
                // If we got a simple success response, construct a response ourselves
                // using the data we sent to ensure the UI is in sync
                return {
                    success: true,
                    message: 'Availability updated successfully',
                    date_overrides: params.date_overrides || [],
                    weekly_schedule: params.weekly_schedule || {},
                    lesson_duration: params.lesson_duration || parseInt(params.duration || '30', 10),
                    needsRefresh: true
                };
            }
        }
        catch (apiError) {
            // Handle timeout/abort errors specially
            if (apiError.name === 'AbortError') {
                console.warn('API request for availability update timed out, using mock response');
                return {
                    success: true,
                    message: 'Availability update timed out, but local changes were saved',
                    date_overrides: params.date_overrides || [],
                    weekly_schedule: params.weekly_schedule || {},
                    lesson_duration: params.lesson_duration || 30
                };
            }
            // Handle authentication errors
            if (apiError.response?.status === 401) {
                console.error('Authentication required for tutor availability update');
                throw new Error('Authentication required - please log in again');
            }
            // Handle validation errors
            if (apiError.response?.status === 400) {
                console.error('Validation error during availability update:', apiError.response.data);
                throw new Error(apiError.response.data.message || 'Invalid availability data');
            }
            // Handle connection errors
            if (apiError.code === 'ECONNABORTED' || apiError.code === 'ERR_NETWORK') {
                console.warn('Network connection error during availability update:', apiError);
                return {
                    success: true,
                    message: 'Network error, but local changes were saved',
                    date_overrides: params.date_overrides || [],
                    weekly_schedule: params.weekly_schedule || {},
                    lesson_duration: params.lesson_duration || 30
                };
            }
            // For other errors, throw to be caught by outer catch
            throw apiError;
        }
    }
    catch (error) {
        console.error('Error updating tutor availability:', error);
        return rejectWithValue(error.message || 'Failed to update availability');
    }
});
// Fetch available tutors (for student dashboard)
export const fetchAvailableTutors = createAsyncThunk('tutors/fetchAvailable', async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get('/tutors/available');
        return response.data;
    }
    catch (error) {
        if (axios.isAxiosError(error)) {
            const errorMessage = error.response?.data?.message || error.message || 'Error fetching tutors';
            return rejectWithValue(errorMessage);
        }
        return rejectWithValue('An unexpected error occurred');
    }
});
// Fetch current tutor profile
export const fetch_current_tutor = createAsyncThunk('tutors/fetchCurrentTutor', async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get('/tutors/settings');
        return response.data.data;
    }
    catch (error) {
        console.error('❌ fetch_current_tutor: Error:', error);
        return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch tutor');
    }
});
// Initial state with snake_case properties
const initialState = {
    tutors: [],
    current_tutor: null,
    search_results: [],
    status: 'idle',
    booking: [],
    availability: {},
    error: null,
    schedule: [],
    is_loading: false
};
// Create the slice
const tutorSlice = createSlice({
    name: 'tutors',
    initialState,
    reducers: {
        clear_tutor_errors: (state) => {
            state.error = null;
        },
        set_current_tutor: (state, action) => {
            state.current_tutor = action.payload;
        },
        clear_current_tutor: (state) => {
            state.current_tutor = null;
        },
        clearSearchResults: (state) => {
            state.search_results = [];
        },
    },
    extraReducers: (builder) => {
        builder
            // Search tutors cases
            .addCase(searchTutors.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(searchTutors.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.search_results = action.payload;
            state.error = null;
        })
            .addCase(searchTutors.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'An error occurred';
        })
            // Age-appropriate search cases
            .addCase(searchAgeAppropriateTutors.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(searchAgeAppropriateTutors.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.search_results = action.payload;
            state.error = null;
        })
            .addCase(searchAgeAppropriateTutors.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'An error occurred';
        })
            // Book tutor cases
            .addCase(bookTutor.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(bookTutor.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.booking.push(action.payload);
            state.error = null;
        })
            .addCase(bookTutor.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'An error occurred';
        })
            // Update tutor settings cases
            .addCase(updateTutorSettings.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(updateTutorSettings.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.current_tutor = action.payload;
            state.error = null;
        })
            .addCase(updateTutorSettings.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'An error occurred';
        })
            // Fetch tutor schedule cases
            .addCase(fetchTutorSchedule.pending, (state) => {
            state.is_loading = true;
            state.error = null;
        })
            .addCase(fetchTutorSchedule.fulfilled, (state, action) => {
            state.is_loading = false;
            // Handle both null (no schedule) and array (has schedule) cases
            if (action.payload === null) {
                state.schedule = [];
                // Only provide an informational message, not an error
                console.info('No schedule data received from API');
            }
            else {
                state.schedule = action.payload;
            }
            // Clear any previous error
            state.error = null;
        })
            .addCase(fetchTutorSchedule.rejected, (state, action) => {
            state.is_loading = false;
            state.error = action.payload || 'An error occurred';
        })
            // Update tutor availability cases
            .addCase(updateTutorAvailability.pending, (state) => {
            state.is_loading = true;
        })
            .addCase(updateTutorAvailability.fulfilled, (state, action) => {
            state.is_loading = false;
            // Type safety - make sure we cast to the correct type
            if (action.payload) {
                state.availability = action.payload;
            }
            state.error = null;
        })
            .addCase(updateTutorAvailability.rejected, (state, action) => {
            state.is_loading = false;
            state.error = action.payload || 'An error occurred';
        })
            // Fetch tutor availability cases
            .addCase(fetchTutorAvailability.pending, (state) => {
            state.is_loading = true;
        })
            .addCase(fetchTutorAvailability.fulfilled, (state, action) => {
            state.is_loading = false;
            // If using a custom state property for availability
            if (!state.availability) {
                state.availability = {};
            }
            // Store the availability data
            const date = toLocalDateKey(new Date());
            state.availability[date] = action.payload;
            state.error = null;
        })
            .addCase(fetchTutorAvailability.rejected, (state, action) => {
            state.is_loading = false;
            state.error = action.payload || 'An error occurred';
        })
            // Fetch available tutors cases
            .addCase(fetchAvailableTutors.pending, (state) => {
            state.is_loading = true;
        })
            .addCase(fetchAvailableTutors.fulfilled, (state, action) => {
            state.is_loading = false;
            state.error = null;
        })
            .addCase(fetchAvailableTutors.rejected, (state, action) => {
            state.is_loading = false;
            state.error = action.payload || 'An error occurred';
        })
            // Fetch current tutor profile cases
            .addCase(fetch_current_tutor.pending, (state) => {
            state.is_loading = true;
            state.error = null;
        })
            .addCase(fetch_current_tutor.fulfilled, (state, action) => {
            state.is_loading = false;
            state.current_tutor = action.payload;
            state.error = null;
        })
            .addCase(fetch_current_tutor.rejected, (state, action) => {
            state.is_loading = false;
            state.error = action.payload || 'An error occurred';
        });
    },
});
export const { clear_tutor_errors, set_current_tutor, clear_current_tutor, clearSearchResults } = tutorSlice.actions;
// Create selectors for accessing tutor state
export const selectCurrentTutor = (state) => state.tutors.current_tutor;
export const selectSearchResults = (state) => state.tutors.search_results;
export const selectTutorStatus = (state) => state.tutors.status;
export const selectTutorError = (state) => state.tutors.error;
// Async thunk for updating tutor settings
export const updateTutorSettings = createAsyncThunk('tutor/updateSettings', async (settings, { rejectWithValue }) => {
    try {
        // Format the payload with proper conversion of fields
        const backendData = formToApiPayload(settings);
        // Ensure subjects is always an array
        if (backendData.subjects) {
            backendData.subjects = ensureSubjectsArray(backendData.subjects);
        }
        const response = await axiosInstance.put('/tutors/settings', backendData);
        // Handle response with normalized helper
        const responseData = normalizeApiResponse(response);
        // Extract the tutor data from the response
        const tutorData = responseData.data;
        // Return properly formatted tutor data
        return normalizeApiPayload(tutorData, {
            id: '',
            first_name: '',
            last_name: '',
            email: '',
            bio: '',
            hourly_rate: 0,
            subjects: [],
        });
    }
    catch (error) {
        return rejectWithValue(handleApiError(error));
    }
});
// Async thunk for age-appropriate tutor search
export const searchAgeAppropriateTutors = createAsyncThunk('tutor/searchAgeAppropriateTutors', async ({ search_term, subject, studentAge }, { rejectWithValue }) => {
    try {
        let queryParams = `term=${search_term}&subject=${subject}&studentAge=${studentAge}`;
        const response = await axiosInstance.get(`/tutors/age-appropriate-search?${queryParams}`);
        // Process API response using normalized helper
        const tutors = normalizeApiResponse(response);
        // Ensure proper data formatting and snake_case
        return tutors.map((tutor) => normalizeApiPayload(tutor, {
            id: '',
            first_name: '',
            last_name: '',
            email: '',
            bio: '',
            hourly_rate: 0,
            subjects: [],
            age_appropriateness: {
                isAppropriate: false,
                reasons: [],
                warnings: []
            },
            safety_features: {
                backgroundCheck: false,
                parentMonitoring: false,
                contentFiltering: false,
                emergencySupport: false,
                sessionRecording: false,
                chatModeration: false
            },
            available_time_slots: [],
            meeting_guidelines: []
        }));
    }
    catch (error) {
        return rejectWithValue(handleApiError(error));
    }
});
// Helper functions to normalize API data
// Simple implementation of normalizeApiPayload function
function normalizeApiPayload(data, defaultValues = {}) {
    return { ...defaultValues, ...data };
}
// Simple implementation of formToApiPayload function
function formToApiPayload(data) {
    return data;
}
export default tutorSlice.reducer;
